import { useCallback, useEffect, useRef, useState } from "react";
import { updateDb } from "../services/updateDb";

export const OTHER_LANGUAGE = { nl: 'en', en: 'nl' };
const TRANSLATABLE_FIELDS = ['subject', 'header', 'body', 'buttonText', 'outro'];
const AUTO_TRANSLATE_DELAY = 1500;
const STORAGE_KEY = 'mailing.autoTranslate';

export const readAutoTranslate = () => {
  try { return localStorage.getItem(STORAGE_KEY) !== 'off'; } catch (e) { return true; }
};

export const storeAutoTranslate = (enabled) => {
  try { localStorage.setItem(STORAGE_KEY, enabled ? 'on' : 'off'); } catch (e) { /* private mode */ }
};

const toRequestContent = (content = {}) => ({
  subject: content.subject || '',
  header: content.header || '',
  body: content.body || '',
  buttonText: content.buttonText || '',
  buttonUrl: content.buttonUrl || '',
  outro: content.outro || '',
});

/**
 * AI translation between the Dutch and English version of a mailing.
 * Fields edited in one language are queued and translated into the other language after a short pause
 * (when auto-translate is on). `onChange` must accept an updater `(prev) => next`, so a translation that
 * arrives later never overwrites newer text: a field is only replaced when it still has the value it had
 * when the translation was requested.
 */
const useMailTranslator = ({ content, onChange, autoTranslate }) => {
  const latest = useRef({ content, onChange });
  latest.current = { content, onChange };
  const pending = useRef({ nl: new Set(), en: new Set() });
  const timer = useRef(null);
  const mounted = useRef(true);
  const [queued, setQueued] = useState(null);
  const [busy, setBusy] = useState({ nl: 0, en: 0 });
  const [error, setError] = useState(null);
  // Fields filled by AI that still need a human check: { en: { body: ['{{name}}'] } } (missing tokens, if any)
  const [aiFields, setAiFields] = useState({ nl: {}, en: {} });
  // Every request and every user edit gets a number per target field; only the newest one may write that field
  const sequence = useRef(0);
  const owner = useRef({ nl: {}, en: {} });
  // Last text the AI wrote per field, so a newer translation may replace an older one
  const aiWritten = useRef({ nl: {}, en: {} });
  // Bumped on reset: answers to earlier requests are then ignored
  const generation = useRef(0);

  const translate = useCallback(async (from, fields, { copyUrl = false } = {}) => {
    const to = OTHER_LANGUAGE[from];
    const gen = generation.current;
    const id = ++sequence.current;
    fields.forEach((field) => { owner.current[to][field] = id; });
    const current = latest.current.content || {};
    const snapshot = Object.fromEntries(fields.map((field) => [field, current[to]?.[field] || '']));
    setBusy((value) => ({ ...value, [to]: value[to] + 1 }));
    const res = await updateDb('/api/mailings/translate', { content: toRequestContent(current[from]), from, to, fields });
    if (mounted.current) setBusy((value) => ({ ...value, [to]: value[to] - 1 }));
    if (gen !== generation.current) return false;
    const isCurrent = (field) => owner.current[to][field] === id;

    if (!res.success) {
      // Fields the user edited (or a newer request took over) in the meantime are not retried
      fields.filter(isCurrent).forEach((field) => pending.current[from].add(field));
      if (mounted.current) setError({ to, message: res.error });
      return false;
    }

    const isUntouched = (target, field) => {
      const value = target?.[field] || '';
      return value === snapshot[field] || (field in aiWritten.current[to] && value === aiWritten.current[to][field]);
    };
    const usable = Object.keys(res.data.content).filter(isCurrent);
    const applied = usable.filter((field) => isUntouched(latest.current.content?.[to], field));
    latest.current.onChange((prev) => {
      const target = { ...(prev?.[to] || {}) };
      usable.forEach((field) => {
        if (!isUntouched(prev?.[to], field)) return;
        target[field] = res.data.content[field];
        aiWritten.current[to][field] = res.data.content[field];
      });
      if (copyUrl && !target.buttonUrl && prev?.[from]?.buttonUrl) target.buttonUrl = prev[from].buttonUrl;
      return { ...prev, [to]: target };
    });

    if (mounted.current) {
      const missing = Object.fromEntries((res.data.warnings || []).map((warning) => [warning.field, warning.missing]));
      setAiFields((value) => {
        const next = { ...value[to] };
        applied.forEach((field) => {
          if (res.data.content[field]) next[field] = missing[field] || [];
          else delete next[field];
        });
        return { ...value, [to]: next };
      });
      setError(null);
    }
    return true;
  }, []);

  const flush = useCallback(() => {
    clearTimeout(timer.current);
    timer.current = null;
    if (mounted.current) setQueued(null);
    Object.keys(pending.current).forEach((from) => {
      const fields = [...pending.current[from]];
      if (fields.length === 0) return;
      pending.current[from] = new Set();
      translate(from, fields);
    });
  }, [translate]);

  // Call for every edit the user makes in `language`
  const markEdited = useCallback((language, field) => {
    // The user now owns this field: it is no longer an AI text and queued or running translations into it are dropped
    pending.current[OTHER_LANGUAGE[language]].delete(field);
    owner.current[language][field] = ++sequence.current;
    delete aiWritten.current[language][field];
    setAiFields((value) => {
      if (!(field in value[language])) return value;
      const next = { ...value[language] };
      delete next[field];
      return { ...value, [language]: next };
    });
    if (!autoTranslate || !TRANSLATABLE_FIELDS.includes(field)) return;
    pending.current[language].add(field);
    setQueued(OTHER_LANGUAGE[language]);
    clearTimeout(timer.current);
    timer.current = setTimeout(flush, AUTO_TRANSLATE_DELAY);
  }, [autoTranslate, flush]);

  const translateAll = useCallback((from) => {
    pending.current[from] = new Set();
    pending.current[OTHER_LANGUAGE[from]] = new Set();
    clearTimeout(timer.current);
    setQueued(null);
    return translate(from, TRANSLATABLE_FIELDS, { copyUrl: true });
  }, [translate]);

  const clear = useCallback(() => {
    generation.current += 1;
    aiWritten.current = { nl: {}, en: {} };
    pending.current = { nl: new Set(), en: new Set() };
    clearTimeout(timer.current);
    setQueued(null);
    setError(null);
    setAiFields({ nl: {}, en: {} });
  }, []);

  useEffect(() => {
    if (autoTranslate) return;
    pending.current = { nl: new Set(), en: new Set() };
    clearTimeout(timer.current);
    setQueued(null);
  }, [autoTranslate]);

  // Switching to another mail unmounts the editor: still translate what was typed just before
  useEffect(() => {
    mounted.current = true;
    return () => {
      if (timer.current) flush();
      mounted.current = false;
    };
  }, [flush]);

  const translatingTo = ['nl', 'en'].find((language) => busy[language] > 0 || queued === language) || null;

  return { markEdited, translateAll, retry: flush, clear, translatingTo, error, aiFields };
};

export default useMailTranslator;
